/**
 * Task Shared Constants - Compatibility Layer
 * This file now imports from the centralized taskConfig for consistency
 * while maintaining backward compatibility with existing components
 */
import {
  DEFAULT_FIELD_SETS,
  getColumnAlignment,
  getColumnWidths,
  getCommonClasses,
  getFieldsForVariant,
  LAYOUT_CONFIG,
  STATUS_TYPE_CONFIG,
  TASK_VARIANT_STYLES,
} from '@/config/taskConfig'

import { ProjectTypeEnum, TaskPriorityEnum } from '@scrambl/shared'

// === RE-EXPORTED CONFIGURATIONS ===
// These maintain backward compatibility for existing components

// Status Type Icons (extracted from centralized config)
export const STATUS_TYPE_ICONS = {
  COMPLETED: STATUS_TYPE_CONFIG.COMPLETED.icon,
  IN_PROGRESS: STATUS_TYPE_CONFIG.IN_PROGRESS.icon,
  ON_HOLD: STATUS_TYPE_CONFIG.ON_HOLD.icon,
  CANCELED: STATUS_TYPE_CONFIG.CANCELED.icon,
  NOT_STARTED: STATUS_TYPE_CONFIG.NOT_STARTED.icon,
} as const

// Status Type Colors (extracted from centralized config)
export const STATUS_TYPE_COLORS = {
  COMPLETED: STATUS_TYPE_CONFIG.COMPLETED.colorDetailed,
  IN_PROGRESS: STATUS_TYPE_CONFIG.IN_PROGRESS.colorDetailed,
  ON_HOLD: STATUS_TYPE_CONFIG.ON_HOLD.colorDetailed,
  CANCELED: STATUS_TYPE_CONFIG.CANCELED.colorDetailed,
  NOT_STARTED: STATUS_TYPE_CONFIG.NOT_STARTED.colorDetailed,
} as const

// Priority Configuration (re-exported from centralized config)
export { PRIORITY_CONFIG } from '@/config/taskConfig'

// Priority Options (re-exported from centralized config)
export const PRIORITY_OPTIONS = [
  { label: 'Low', value: TaskPriorityEnum.LOW },
  { label: 'Medium', value: TaskPriorityEnum.MEDIUM },
  { label: 'High', value: TaskPriorityEnum.HIGH },
  { label: 'Urgent', value: TaskPriorityEnum.URGENT },
] as const

// Default Status Options (re-exported from centralized config)
export { DEFAULT_STATUS_OPTIONS } from '@/config/taskConfig'

// Task Item Variant Styles (re-exported from centralized config)
export const TASK_ITEM_VARIANT_STYLES = TASK_VARIANT_STYLES

// Common UI Class Names (using centralized function)
export const COMMON_CLASSES = getCommonClasses()

// Task Field Types
export type TaskFieldType =
  | 'status'
  | 'priority'
  | 'assignees'
  | 'scheduledFor'
  | 'dueDate'
  | 'storyPoints'
  | 'estimatedHours'
  | 'dependencies'
  | 'comments'
  | 'group'
  | 'subtasks'
  | 'tags'
  | 'deployment'

// Default Field Sets (re-exported from centralized config)
export const DEFAULT_FIELDS = DEFAULT_FIELD_SETS

// Get field sets based on project type (re-exported from centralized config)
export const getFieldsForProjectType = getFieldsForVariant

// Column widths for consistent layout (using centralized function)
export const COLUMN_WIDTHS = getColumnWidths()

// Get column widths based on project type and subtask status (re-exported from centralized config)
export const getColumnWidthsForProjectType = getColumnWidths

// Get layout gaps based on project type (using centralized config)
export const getLayoutGapsForProjectType = (
  projectType?: string,
  isSubtask?: boolean,
) => {
  if (isSubtask) {
    return {
      list: 'gap-2',
      card: 'gap-2',
      table: 'gap-2',
      compact: 'gap-2',
    }
  }

  if (projectType === ProjectTypeEnum.SCRUM) {
    return {
      list: 'gap-1.5',
      card: 'gap-1.5',
      table: 'gap-1.5',
      compact: 'gap-1',
    }
  }
  return LAYOUT_CONFIG.gaps
}

// Table Grid Template for CSS Grid Layout
export const TABLE_GRID_TEMPLATE = {
  list: 'grid-cols-[16px_20px_1fr_112px_64px_80px_112px_40px]',
  table: 'grid-cols-[16px_20px_1fr_112px_64px_80px_112px_64px_40px]',
  compact: 'grid-cols-[16px_20px_1fr_112px_64px_40px]',
} as const

// Spacing and Layout Constants (re-exported from centralized config)
export const LAYOUT = LAYOUT_CONFIG

export type TaskVariant = keyof typeof TASK_ITEM_VARIANT_STYLES

// Field Order Configuration for consistent column alignment
export const FIELD_ORDER = {
  list: [
    'status',
    'priority',
    'assignees',
    'dueDate',
    'subtasks',
    'comments',
    'dependencies',
  ] as TaskFieldType[],
  table: [
    'status',
    'priority',
    'assignees',
    'dueDate',
    'storyPoints',
    'group',
    'subtasks',
    'comments',
    'dependencies',
  ] as TaskFieldType[],
  compact: [
    'status',
    'priority',
    'subtasks',
    'comments',
    'dependencies',
  ] as TaskFieldType[],
} as const

// Column alignment for consistent styling (using centralized function)
export const COLUMN_ALIGNMENT = getColumnAlignment(false) // subtask version (with minimal top padding)
export const COLUMN_ALIGNMENT_CENTERED = getColumnAlignment(true) // regular tasks (centered, no top padding)

// Column separator styles for visual separation
export const COLUMN_SEPARATOR = 'border-r border-border/30' as const
